import React, { useMemo, useState } from "react";
import { Button, Col, Collapse, DatePicker, Form, Input, Row, Select, Space, Spin } from "antd";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../../..";
import {
  checkIsLoading,
  displayFormat,
  getSelectOptions,
  useActiveModal,
} from "../../../../lib/utils";
import "./TypesCharacteristicsObjectListSearch.less";
import { BulkFiltersToggler } from "../../../../components/BulkFiltersToggler/BulkFiltersToggler";
import { searchIconForInput } from "../../../../lib/icons";
import { useTypesCharacteristicsObjectContext } from "../../context/TypesCharacteristicsObjectContext/TypesCharacteristicsObjectContext";
import { ISearchFilterField } from "../../../../lib/types";
import {
  searchTypesCharacteristics,
  typesCharacteristicsObjectActions,
} from "../../store/slice/typesCharacteristicsSlice";

/* ------------------------------------------------------ Начало компонента ------------------------------------------------------ */

export const TypesCharacteristicsObjectListSearch = () => {
  const {
    isSearchOpen,
    toggleSearchOpen,
    handleTypesCharacteristicsObjectListSearch,
    typesCharacteristicsObjectListSearchForm,
  } = useTypesCharacteristicsObjectContext();

  const {
    typesCharacteristics,
    typesCharacteristicsLoading,
    typesCharacteristicsObjectLoading,
    typesCharacteristicsObjectObjectTypesDic,
    typesCharacteristicsObjectObjectTypesDicLoading,
  } = useAppSelector((state) => state.typesCharacteristicsObject);

  const dispatch = useAppDispatch();

  const [valueCharType, setValueCharType] = useState<string>("");

  const handleSearchCharType = async (value: string) => {
    setValueCharType(value);

    if (value.length < 3) return; // Не искать, если менее 3 символов

    dispatch(searchTypesCharacteristics({ name: value }));
  };

  const [selectedCharType, setSelectedCharType] = useState<number>();

  const onChangeCharType = (value) => {
    setSelectedCharType(value);
  };

  const onBlurCharType = () => {
    if (!selectedCharType) {
      dispatch(typesCharacteristicsObjectActions.cleanTypesCharacteristics());
    }
  };

  const onClearCharType = () => {
    dispatch(typesCharacteristicsObjectActions.cleanTypesCharacteristics());
    setSelectedCharType(undefined);
  };

  const allFields: ISearchFilterField[] = useMemo(
    () => [
      {
        label: "Дата",
        fieldElement: (
          <Form.Item label="Дата" name="onDate">
            <DatePicker format={displayFormat} allowClear />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Тип характеристики",
        fieldElement: (
          <Form.Item label="Тип характеристики" name="charTypeIds">
            <Select
              options={getSelectOptions({
                list: valueCharType.length > 2 ? typesCharacteristics : [],
              })}
              loading={checkIsLoading(typesCharacteristicsLoading)}
              style={{ width: "100%" }}
              placeholder="Введите текст"
              showArrow={false}
              showSearch
              allowClear
              onSearch={handleSearchCharType} // Вызов при вводе текста
              filterOption={false} // Отключаем стандартную фильтрацию
              onChange={onChangeCharType}
              onBlur={onBlurCharType}
              onClear={onClearCharType}
            />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Объект",
        fieldElement: (
          <Form.Item label="Объект" name="objectTypeIds">
            <Select
              options={getSelectOptions({
                list: [...typesCharacteristicsObjectObjectTypesDic].sort((a, b) =>
                  a?.name?.localeCompare(b?.name)
                ),
              })}
              loading={checkIsLoading(typesCharacteristicsObjectObjectTypesDicLoading)}
              style={{ width: "100%" }}
              placeholder="Выбрать"
              mode="multiple"
              allowClear
              showArrow
              showSearch
              filterOption={(inputValue, option) => {
                return String(option?.label)?.toLowerCase().includes(inputValue?.toLowerCase());
              }}
            />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Наименование",
        fieldElement: (
          <Form.Item label="Наименование" name="name">
            <Input placeholder="Введите текст..." allowClear />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "ID Типов характеристик (объект)",
        fieldElement: (
          <Form.Item
            label="ID Типов характеристик (объект)"
            name="objectCharTypeIds"
            style={{ marginBottom: 0 }}
            className="form-item-required__hidden-error__mb0 whiteSpase-normal"
            labelCol={{ span: 11 }}
            wrapperCol={{ span: 13 }}
            rules={[
              {
                validator: (x, value) => {
                  if (value && /[^0-9,]/.test(value)) {
                    return Promise.reject();
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              aria-autocomplete="none"
              placeholder="Введите ID Типов характеристик (объект) через “,”"
              className="table-head-filter-search"
              allowClear
              suffix={searchIconForInput()}
            />
          </Form.Item>
        ),
        visible: false,
      },
      {
        label: "Alias",
        fieldElement: (
          <Form.Item label="Alias" name="aliases">
            <Input placeholder="Введите текст..." allowClear />
          </Form.Item>
        ),
        visible: false,
      },
    ],
    [
      selectedCharType,
      typesCharacteristics,
      typesCharacteristicsLoading,
      typesCharacteristicsObjectObjectTypesDic,
      typesCharacteristicsObjectObjectTypesDicLoading,
    ]
  );

  const [fields, setFields] = useState<ISearchFilterField[]>(allFields);

  return (
    <div className="types-characteristics-object-list-search">
      <Collapse
        expandIconPosition="right"
        activeKey={isSearchOpen ? "1" : ""}
        onChange={toggleSearchOpen}
      >
        <Collapse.Panel header="Расширенный поиск" key="1">
          <Row justify="end">
            <BulkFiltersToggler
              allFields={allFields}
              setFields={setFields}
              filterName="types-characteristics-object-list"
              minSave={["Наименование"]}
            />
          </Row>
          <Form
            form={typesCharacteristicsObjectListSearchForm}
            onFinish={handleTypesCharacteristicsObjectListSearch}
            wrapperCol={{ span: 17 }}
            labelCol={{ span: 7 }}
            labelAlign="left"
            onReset={() => {
              typesCharacteristicsObjectListSearchForm.resetFields();
              dispatch(typesCharacteristicsObjectActions.cleanTypesCharacteristics());
              setSelectedCharType(undefined);
            }}
            initialValues={{
              onDate: moment(),
            }}
          >
            <Row gutter={50}>
              {fields
                .filter(({ visible }) => visible)
                .map(({ fieldElement }, idx) => (
                  <Col span={8} key={idx}>
                    {fieldElement}
                  </Col>
                ))}
            </Row>
            <Row justify="end">
              <Space size={20}>
                <Button htmlType="reset">Очистить</Button>
                <Spin spinning={checkIsLoading(typesCharacteristicsObjectLoading)}>
                  <Button type="primary" htmlType="submit">
                    Применить
                  </Button>
                </Spin>
              </Space>
            </Row>
          </Form>
        </Collapse.Panel>
      </Collapse>
    </div>
  );
};
